import DataStore from '../services/dataStore';
import { StandardsComparator } from '../services/comparators/StandardsComparator';
import { TimeConverter } from '../services/utils/TimeConverter';
import { authedFetch } from './authedFetch';
import { inferCountry } from '../services/utils/inferCountry';
const dataStore = new DataStore();
const standardsComparator = new StandardsComparator(new TimeConverter());
function migrateCountyTimesStore(raw) {
    if (!raw || typeof raw !== 'object')
        return {};
    const currentYear = new Date().getFullYear();
    const result = {};
    for (const [key, value] of Object.entries(raw)) {
        const v = value;
        const derivedCountyName = key.replace(/\s+\d{4}$/, '').trim();
        if (v && typeof v === 'object' && typeof v['year'] === 'number' && v['times'] && typeof v['times'] === 'object') {
            result[key] = {
                countyName: v['countyName'] ?? derivedCountyName,
                year: v['year'],
                country: inferCountry(key),
                times: v['times'],
            };
        }
        else {
            result[key] = { countyName: derivedCountyName, year: currentYear, country: inferCountry(key), times: value };
        }
    }
    return result;
}
function toRankingsRequest(s) {
    return {
        tiref: s.tiref,
        name: s.name,
        club: s.club,
        region: s.region,
        birthYear: s.birthYear,
        gender: s.gender,
        times: s.times,
    };
}
export async function apiFetch(input, init) {
    const res = await authedFetch(input, init);
    if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`API error ${res.status}: ${body}`);
    }
    return res.json();
}
async function fetchUserData() {
    return apiFetch('/api/user/data');
}
async function putUserData(body) {
    return apiFetch('/api/user/data', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
}
async function fetchProfile() {
    const data = await fetchUserData();
    return { homeClub: data.homeClub, role: data.role };
}
async function persistProfile(profile) {
    const data = await putUserData(profile);
    return { homeClub: data.homeClub, role: data.role };
}
export const mobileAPI = {
    searchSwimmer(name) {
        return apiFetch(`/api/search?surname=${encodeURIComponent(name)}`);
    },
    getSwimmerTimes(tiref) {
        return apiFetch(`/api/swimmer/${encodeURIComponent(tiref)}`);
    },
    getTimeHistory(sourceUrl) {
        return apiFetch(`/api/swimmer/history?url=${encodeURIComponent(sourceUrl)}`);
    },
    getSplits(splitsUrl) {
        return apiFetch(`/api/swimmer/splits?url=${encodeURIComponent(splitsUrl)}`);
    },
    async saveSwimmer(swimmerData) {
        try {
            const saved = await apiFetch(`/api/user/swimmers/${encodeURIComponent(swimmerData.tiref)}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(swimmerData),
            });
            await dataStore.saveSwimmer(saved);
            return saved;
        }
        catch (err) {
            console.warn('saveSwimmer: backend unavailable, writing local only', err);
            return dataStore.saveSwimmer(swimmerData);
        }
    },
    async getSavedSwimmers() {
        try {
            const swimmers = await apiFetch('/api/user/swimmers');
            await dataStore.replaceAllSwimmers(swimmers);
            return swimmers;
        }
        catch (err) {
            console.warn('getSavedSwimmers: falling back to local cache', err);
            return dataStore.getAllSwimmers();
        }
    },
    async deleteSwimmer(tiref) {
        try {
            const result = await apiFetch(`/api/user/swimmers/${encodeURIComponent(tiref)}`, { method: 'DELETE' });
            await dataStore.deleteSwimmer(tiref);
            return result.deleted;
        }
        catch (err) {
            console.warn('deleteSwimmer: backend unavailable, deleting local only', err);
            return dataStore.deleteSwimmer(tiref);
        }
    },
    async exportToExcel(swimmerData, comparisonResult) {
        const res = await authedFetch('/api/export/excel', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...swimmerData, comparisonResult }),
        });
        if (!res.ok)
            throw new Error(`Export failed: ${await res.text()}`);
        const fileName = res.headers.get('Content-Disposition')?.match(/filename="([^"]+)"/)?.[1] ?? 'export.xlsx';
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        return fileName;
    },
    compareWithCountyTimes(swimmerData, countyTimes) {
        return Promise.resolve(standardsComparator.compareWithStandards(swimmerData, countyTimes));
    },
    pickCountyTimesFile() {
        return new Promise((resolve) => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.pdf,.xlsx,.xls';
            input.multiple = true;
            input.onchange = async () => {
                if (!input.files || !input.files.length) {
                    resolve(null);
                    return;
                }
                const loaded = [];
                for (const file of Array.from(input.files)) {
                    try {
                        const res = await authedFetch('/api/import', {
                            method: 'POST',
                            headers: { 'x-filename': encodeURIComponent(file.name) },
                            body: file,
                        });
                        if (!res.ok)
                            throw new Error(`Import failed: ${await res.text()}`);
                        const entries = await res.json();
                        loaded.push(...entries.map(entry => ({ ...entry, fileName: file.name })));
                    }
                    catch (error) {
                        console.error(`Error processing file ${file.name}:`, error);
                    }
                }
                resolve(loaded.length ? loaded : null);
            };
            input.oncancel = () => resolve(null);
            input.click();
        });
    },
    async saveCountyTimesStore(store) {
        console.debug('saveCountyTimesStore: saving countyTimesStore to backend', Object.keys(store));
        try {
            await putUserData({ countyTimesStore: store });
            await dataStore.saveCountyTimesStore(store);
            console.debug('saveCountyTimesStore: saved to backend and local cache');
        }
        catch (err) {
            console.warn('saveCountyTimesStore: backend unavailable, writing local only', err);
            await dataStore.saveCountyTimesStore(store);
        }
    },
    async loadCountyTimesStore() {
        try {
            const data = await fetchUserData();
            const migrated = migrateCountyTimesStore(data.countyTimesStore);
            await dataStore.saveCountyTimesStore(migrated);
            console.debug('loadCountyTimesStore: loaded from backend', Object.keys(migrated));
            return migrated;
        }
        catch (err) {
            console.warn('loadCountyTimesStore: falling back to local cache', err);
            const raw = await dataStore.loadCountyTimesStore();
            const migrated = migrateCountyTimesStore(raw);
            console.debug('loadCountyTimesStore: loaded from local cache', Object.keys(migrated));
            return migrated;
        }
    },
    getSwimmerRankings(swimmerData, level, ages, countyCode, regionCode) {
        return apiFetch('/api/rankings/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...toRankingsRequest(swimmerData), level, ages, countyCode, regionCode })
        });
    },
    getClubLeaderboard(params) {
        return apiFetch('/api/rankings/leaderboard', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(params)
        });
    },
    async saveActiveStandards(active) {
        try {
            await putUserData({ activeStandards: active });
            await dataStore.saveActiveStandards(active);
        }
        catch (err) {
            console.warn('saveActiveStandards: backend unavailable, writing local only', err);
            await dataStore.saveActiveStandards(active);
        }
    },
    async loadActiveStandards() {
        try {
            const data = await fetchUserData();
            await dataStore.saveActiveStandards(data.activeStandards);
            return data.activeStandards;
        }
        catch (err) {
            console.warn('loadActiveStandards: falling back to local cache', err);
            return dataStore.loadActiveStandards();
        }
    },
    saveSelectedStrokes(strokes) {
        return dataStore.saveSelectedStrokes(strokes);
    },
    loadSelectedStrokes() {
        return dataStore.loadSelectedStrokes();
    },
    saveSelectedDistances(distances) {
        return dataStore.saveSelectedDistances(distances);
    },
    loadSelectedDistances() {
        return dataStore.loadSelectedDistances();
    },
    getProfile() {
        return fetchProfile();
    },
    saveProfile(profile) {
        return persistProfile(profile);
    },
};
export const _internalDataStore = dataStore;
